import React, { useState } from 'react';
import axios from 'axios';
import ParticipantForm from './ParticipantForm';

// Component for secret token in grid
const SecretToken = ({ token }) => {
  const [visible, setVisible] = useState(false);
  if (!token) return <span className="text-muted small">---</span>;
  return (
    <code
      className={`cursor-pointer px-2 py-1 rounded small ${visible ? 'bg-primary text-white' : 'bg-light text-muted blur-text'}`}
      style={!visible ? { filter: 'blur(4px)', userSelect: 'none' } : {}}
      onClick={() => setVisible(!visible)}
      title={visible ? "Clicca per nascondere" : "Clicca per mostrare"}
    >
      {token}
    </code>
  );
};

export default function AdminPanel({ participants, onRefresh, eventStatus, eventId, onEditParticipant, onVoteAs }) {
  const [sortBy, setSortBy] = useState('arrival');

  const sortedParticipants = [...participants].sort((a, b) => {
    if (sortBy === 'name') {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    }
    // Default: sort by arrival number
    return (a.arrivalNumber || 999) - (b.arrivalNumber || 999);
  });


  const handleCheckIn = async (p) => {
    try {
      const res = await axios.post(`/speeddate/api/participants/${p.id}/check-in`);
      const updated = res.data;

      window.Swal.fire({
        title: `<h4 class="fw-bold mb-0 text-primary">Arrivo Registrato!</h4>`,
        html: `
                <div class="py-4 px-2">
                    <p class="mb-2 text-muted small text-uppercase fw-bold">Numero di Arrivo per ${p.sex === 'male' ? 'Uomo' : 'Donna'}</p>
                    <div style="font-size: 5rem; line-height: 1;" class="fw-bold text-dark mb-4 drop-shadow">
                        ${updated.arrivalNumber}
                    </div>
                    
                    <div class="glass p-3 rounded-4 mt-2">
                        <p class="mb-1 text-muted small">CODICE SEGRETO D'ACCESSO</p>
                        <div class="h3 fw-bold text-primary ls-lg mb-0">
                            ${updated.token}
                        </div>
                    </div>
                    <p class="mt-3 text-muted small italic">Comunica questo numero e il codice al partecipante.</p>
                </div>
            `,
        icon: 'success',
        confirmButtonText: 'Capito, grazie!',
        confirmButtonColor: '#6366f1',
        customClass: {
          popup: 'swal2-glass',
          confirmButton: 'rounded-pill px-5 py-2 shadow-lg'
        }
      });

      if (onRefresh) onRefresh();
    } catch (err) {
      window.Swal.fire({
        title: 'Errore',
        text: "Impossibile registrare l'arrivo",
        icon: 'error',
        customClass: { popup: 'swal2-glass' }
      });
    }
  };

  const deleteParticipant = async (id) => {
    window.Swal.fire({
      title: 'Rimuovere partecipante?',
      text: "Questa operazione non può essere annullata.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sì, rimuovi',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#d33',
      customClass: { popup: 'swal2-glass' }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/speeddate/api/participants/${id}`);
          window.Swal.fire({
            title: 'Rimosso!',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false,
            toast: true,
            position: 'top-end',
            customClass: { popup: 'swal2-glass' }
          });
          if (onRefresh) onRefresh();
        } catch (err) {
          window.Swal.fire({
            title: 'Errore',
            text: "Non è stato possibile rimuovere il partecipante.",
            icon: 'error',
            customClass: { popup: 'swal2-glass' }
          });
        }
      }
    });
  };

  const getStatusBadge = (p) => {
    // SOLA-57: email risultati NON consegnabile → evidenziata in rosso.
    // Include i rifiuti del server di destinazione (deliveryStatus='rejected',
    // es. "550 blacklisted IP") anche se il relay locale aveva accettato.
    const matchEmail = p.matchEmail || null;
    const matchEmailFailed = matchEmail && (
      matchEmail.status === 'error' ||
      matchEmail.deliveryStatus === 'rejected' ||
      matchEmail.deliveryStatus === 'bounced'
    );
    if (matchEmailFailed) {
      const failReason = (matchEmail.deliveryDetail || matchEmail.error || '').slice(0, 160);
      return (
        <button
          className="btn btn-sm btn-danger rounded-pill px-3 shadow-sm d-flex align-items-center gap-2"
          onClick={(e) => { e.stopPropagation(); handleResendEmail(p); }}
          title={`Email risultati NON consegnata${failReason ? `: ${failReason}` : ''} - Clicca per inoltrare`}
        >
          <i className="fas fa-envelope-exclamation small"></i>
          <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Non consegnata</span>
        </button>
      );
    }

    const { status, MatchEmailSent, MatchEmailOpened, MatchEmailOpenedAt } = p;

    // If email sent, show email button instead of "Presente" (confirmed)
    if (MatchEmailSent) {
      const label = MatchEmailOpened ? 'Visualizzata' : 'Inviata';
      const color = MatchEmailOpened ? 'info' : 'success';
      const icon = MatchEmailOpened ? 'fa-envelope-open' : 'fa-paper-plane';
      const tooltip = MatchEmailOpened && MatchEmailOpenedAt
        ? `Aperta il ${new Date(MatchEmailOpenedAt).toLocaleString('it-IT')}`
        : (MatchEmailOpened ? 'Visualizzata' : 'Email Inviata');

      return (
        <button
          className={`btn btn-sm btn-${color} rounded-pill px-3 shadow-sm d-flex align-items-center gap-2`}
          onClick={(e) => { e.stopPropagation(); handleResendEmail(p); }}
          title={`${tooltip} - Clicca per inoltrare`}
        >
          <i className={`fas ${icon} small`}></i>
          <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{label}</span>
        </button>
      );
    }

    switch (status) {
      case 'registered': return <span className="badge badge-secondary-subtle px-3 py-2 rounded-pill">Registrato</span>;
      case 'confirmed': return <span className="badge badge-success-subtle px-3 py-2 rounded-pill">Presente</span>;
      case 'cancelled': return <span className="badge badge-danger-subtle px-3 py-2 rounded-pill">Annullato</span>;
      default: return <span className="badge badge-secondary-subtle px-3 py-2 rounded-pill">{status}</span>;
    }
  };

  const handleResendEmail = async (p) => {
    const { value: email } = await window.Swal.fire({
      title: 'Inoltra Risultati',
      text: `Inoltra l'email dei risultati per ${p.firstName} ${p.lastName}`,
      input: 'email',
      inputValue: p.email,
      inputPlaceholder: 'Inserisci indirizzo email',
      showCancelButton: true,
      confirmButtonText: 'Invia',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#6366f1',
      customClass: { popup: 'swal2-glass' }
    });

    if (email) {
      try {
        window.Swal.fire({
          title: 'Invio in corso...',
          allowOutsideClick: false,
          didOpen: () => { window.Swal.showLoading(); }
        });

        await axios.post(`/speeddate/api/participants/${p.id}/resend-results`, { email });

        window.Swal.fire({
          title: 'Inviata!',
          text: `I risultati sono stati inoltrati a ${email}`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
          customClass: { popup: 'swal2-glass' }
        });
      } catch (err) {
        window.Swal.fire({
          title: 'Errore',
          text: err.response?.data?.error || "Impossibile inoltrare l'email",
          icon: 'error',
          customClass: { popup: 'swal2-glass' }
        });
      }
    }
  };

  const handleChangeArrivalNumber = async (p) => {
    const { value: newNum } = await window.Swal.fire({
      title: `Cambia Numero di Arrivo`,
      html: `
        <div class="py-2">
          <p class="text-muted small mb-3">${p.firstName} ${p.lastName} (${p.sex === 'male' ? 'Uomo' : 'Donna'})</p>
          <p class="mb-2">Numero attuale: <strong class="text-primary">${p.arrivalNumber}</strong></p>
          <p class="text-muted small">Inserisci il nuovo numero di arrivo</p>
        </div>
      `,
      input: 'number',
      inputValue: p.arrivalNumber,
      inputPlaceholder: 'Nuovo numero',
      inputAttributes: {
        min: 1,
        step: 1
      },
      showCancelButton: true,
      confirmButtonText: 'Aggiorna',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#6366f1',
      inputValidator: (value) => {
        if (!value || value < 1) {
          return 'Il numero deve essere maggiore di 0';
        }
      },
      customClass: { popup: 'swal2-glass' }
    });

    if (newNum && parseInt(newNum) !== p.arrivalNumber) {
      try {
        await axios.put(`/speeddate/api/participants/${p.id}/arrival-number`, { 
          arrivalNumber: parseInt(newNum) 
        });
        
        window.Swal.fire({
          title: 'Numero aggiornato!',
          text: `Nuovo numero di arrivo: ${newNum}`,
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: 'top-end',
          customClass: { popup: 'swal2-glass' }
        });
        
        if (onRefresh) onRefresh();
      } catch (err) {
        window.Swal.fire({
          title: 'Errore',
          text: err.response?.data?.error || 'Impossibile aggiornare il numero di arrivo',
          icon: 'error',
          customClass: { popup: 'swal2-glass' }
        });
      }
    }
  };

  const getGenderIcon = (sex) => {
    if (sex === 'male') return <i className="fas fa-mars text-primary" title="Maschio"></i>;
    if (sex === 'female') return <i className="fas fa-venus text-danger" title="Femmina"></i>;
    return null;
  };

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0 bg-transparent">
          <thead>
            <tr>
              <th className="small text-uppercase fw-bold text-muted border-0 py-3 ps-4">
                Partecipante
                <button 
                  className="btn btn-link btn-sm p-0 ms-2" 
                  onClick={() => setSortBy(sortBy === 'name' ? 'arrival' : 'name')}
                  title={sortBy === 'name' ? 'Ordina per numero arrivo' : 'Ordina per nome'}
                >
                  <i className={`fas fa-${sortBy === 'name' ? 'sort-alpha-down' : 'sort-numeric-down'} ${sortBy === 'name' ? 'text-primary' : ''}`}></i>
                </button>
              </th>
              <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center">N° Arrivo</th>
              <th className="small text-uppercase fw-bold text-muted border-0 py-3">Codice Segreto</th>
              {eventStatus >= 4 && (
                <>
                  <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center">Inviati</th>
                  <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center">Ricevuti</th>
                  <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center">Match</th>
                  <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center">Magic Ricev.</th>
                  <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center">Magic Match</th>
                </>
              )}
              <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center" title="Stato email reminder (verde = letta, rosso = inviata non letta, vuoto = mai inviata)">
                Reminder
              </th>
              <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-center">Status</th>
              <th className="small text-uppercase fw-bold text-muted border-0 py-3 text-end pe-4">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {participants.length === 0 ? (
              <tr>
                <td colSpan={eventStatus >= 4 ? "11" : "6"} className="text-center py-5">
                  <div className="text-muted">
                    <i className="fas fa-users-slash fa-2x mb-2 opacity-50"></i>
                    <p className="mb-0">Nessun partecipante iscritto</p>
                  </div>
                </td>
              </tr>
            ) : sortedParticipants.map(p => {
              const matchEmailFailed = p.matchEmail && (
                p.matchEmail.status === 'error' ||
                p.matchEmail.deliveryStatus === 'rejected' ||
                p.matchEmail.deliveryStatus === 'bounced'
              );
              return (
              <tr
                key={p.id}
                className={`border-bottom border-light ${matchEmailFailed ? 'table-danger' : ''}`}
                style={{ transition: 'background 0.3s' }}
              >
                <td className="ps-4">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center me-3" style={{ width: '42px', height: '42px', minWidth: '42px' }}>
                      {getGenderIcon(p.sex)}
                    </div>
                    <div>
                      <p className="fw-bold mb-0 text-dark">{p.firstName} {p.lastName}</p>
                      <div className="d-flex flex-column">
                        <span className="text-muted small">{p.email} &bull; {p.age} anni</span>
                        <div className="mt-1 d-flex gap-2">
                          {p.phone && <span className="text-muted small" title="Telefono"><i className="fas fa-phone me-1 text-success opacity-75"></i>{p.phone}</span>}
                          {p.facebook && <span className="text-muted small" title="Facebook"><i className="fab fa-facebook text-primary opacity-75"></i></span>}
                          {p.instagram && <span className="text-muted small" title="Instagram"><i className="fab fa-instagram text-danger opacity-75"></i></span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="text-center">
                  {p.arrivalNumber ? (
                    <div 
                      className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center fw-bold shadow-sm" 
                      style={{ width: '32px', height: '32px', cursor: 'pointer' }}
                      onClick={() => handleChangeArrivalNumber(p)}
                      title="Clicca per cambiare numero"
                    >
                      {p.arrivalNumber}
                    </div>
                  ) : (
                    <span className="text-muted small">---</span>
                  )}
                </td>
                <td>
                  <SecretToken token={p.token} />
                </td>
                {eventStatus >= 4 && (
                  <>
                    <td className="text-center">
                      <span className="badge rounded-pill px-3 bg-light text-primary">
                        <i className="fas fa-heart me-1"></i> {p.likesSent || 0}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="badge rounded-pill px-3 bg-light text-danger">
                        <i className="far fa-heart me-1"></i> {p.likesReceived || 0}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`badge rounded-pill px-3 ${p.matchCount > 0 ? 'bg-success' : 'bg-light text-muted'}`}>
                        <i className="fas fa-bolt me-1"></i> {p.matchCount || 0}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`badge rounded-pill px-3 ${p.magicCount > 0 ? 'bg-warning text-dark' : 'bg-light text-muted'}`} title="Magic Like Ricevuti">
                        <i className="far fa-star me-1"></i> {p.magicCount || 0}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`badge rounded-pill px-3 ${p.magicMatchCount > 0 ? 'bg-warning text-dark border border-warning' : 'bg-light text-muted'}`} title="Match con Magic Like dell'altro">
                        <i className="fas fa-bolt me-1"></i><i className="fas fa-star small"></i> {p.magicMatchCount || 0}
                      </span>
                    </td>
                  </>
                )}
                <td className="text-center">
                  {p.reminder && p.reminder.sent ? (
                    <div
                      className={`rounded-circle ${p.reminder.read ? 'bg-success' : 'bg-danger'}`}
                      style={{ width: '12px', height: '12px', display: 'inline-block' }}
                      title={p.reminder.read
                        ? 'Reminder letto' + (p.reminder.readAt ? ` il ${new Date(p.reminder.readAt).toLocaleString('it-IT')}` : '')
                        : 'Reminder inviato, non ancora letto'}
                    ></div>
                  ) : null}
                </td>
                <td className="text-center">
                  {getStatusBadge(p)}
                </td>
                <td className="text-end pe-4">
                  <div className="d-flex justify-content-end gap-1">
                    {eventStatus < 4 && (
                      <>
                        {eventStatus >= 3 && !p.arrivalNumber && (
                          <button className="btn btn-primary btn-sm px-3 rounded-pill shadow-sm" onClick={() => handleCheckIn(p)}>
                            <i className="fas fa-check me-1"></i> Check-in
                          </button>
                        )}
                        <button className="btn btn-link btn-sm text-primary px-2" onClick={() => onEditParticipant(p)} title="Modifica">
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="btn btn-link btn-sm text-danger px-2" onClick={() => deleteParticipant(p.id)} title="Rimuovi">
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </>
                    )}
                    {eventStatus >= 4 && eventStatus < 7 && (
                      <button className="btn btn-link btn-sm text-warning px-2" onClick={() => onVoteAs(p)} title="Inserisci Preferenze">
                        <i className="fas fa-heart"></i>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
